import { z } from 'zod';
import { isoDateSchema, moneyMinorSchema } from '../api';
import { tenantSettingsSchema } from './org';
import { BILLING_EXEMPTION, HOME_COLLECTION_STATUS, LAB_ORDER_ITEM_STATUS, LAB_RESULT_FLAG, RADIOLOGY_MODALITY, RADIOLOGY_STATUS, } from '../enums';
import { COLLECTION_IDENTITY_METHODS } from '../lab-settings';
export const MAX_BATCH_IDS = 200;
export const MAX_REPORT_RANGE_DAYS = 366;
export const CATALOGUE_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._/+-]*$/;
export const CATALOGUE_CODE_MESSAGE = 'Use letters, digits and . _ / + - only, without spaces';
const cursorText = z.string().max(120).regex(/^[A-Za-z0-9:_.-]+$/, 'Not a valid cursor');
export const LOINC_CODE_PATTERN = /^\d{1,7}-\d$/;
export const ICD_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9.-]{0,19}$/;
export const labOrderInsuranceSchema = z
    .object({
    diagnosisCode: z.string().trim().regex(ICD_CODE_PATTERN, 'Pick the diagnosis from the ICD search').nullable().optional(),
    diagnosisTitle: z.string().trim().min(2).max(300).nullable().optional(),
    referralNo: z.string().trim().min(1).max(60).nullable().optional(),
    referringLicense: z.string().trim().regex(/^[A-Za-z0-9./-]{2,40}$/, 'Enter the licence number without spaces').nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.diagnosisCode && !value.diagnosisTitle)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['diagnosisTitle'], message: 'Pick the diagnosis from the ICD search' });
});
export const referenceRangeSchema = z.object({
    sex: z.enum(['male', 'female', 'any']).default('any'),
    minAgeDays: z.number().int().min(0).default(0),
    maxAgeDays: z.number().int().min(0).default(365 * 200),
    low: z.number().nullable().optional(),
    high: z.number().nullable().optional(),
    text: z.string().max(200).optional(),
});
export const catalogueNameI18nSchema = z.record(z.enum(['ar', 'es', 'ur-Latn']), z.string().trim().max(200));
export const upsertLabTestSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24).regex(CATALOGUE_CODE_PATTERN, CATALOGUE_CODE_MESSAGE),
    name: z.string().trim().min(2).max(200),
    departmentGroup: z.enum([
        'hematology',
        'biochemistry',
        'microbiology',
        'serology',
        'histopathology',
        'molecular',
        'other',
    ]),
    specimenType: z.string().trim().min(2).max(80),
    containerType: z.string().trim().max(80).optional(),
    tatMinutes: z.number().int().min(5).max(60 * 24 * 30).default(240),
    unit: z.string().trim().max(24).optional(),
    resultType: z.enum(['numeric', 'text', 'select', 'culture']).default('numeric'),
    selectOptions: z.array(z.string().max(80)).max(50).default([]),
    abnormalOptions: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
    referenceRanges: z.array(referenceRangeSchema).default([]),
    criticalLow: z.number().nullable().optional(),
    criticalHigh: z.number().nullable().optional(),
    reportableLow: z.number().nullable().optional(),
    reportableHigh: z.number().nullable().optional(),
    method: z.string().trim().max(160).optional(),
    chargeItemId: z.string().uuid().nullable().optional(),
    basePriceMinor: moneyMinorSchema.min(0).default(0),
    isOutsourced: z.boolean().default(false),
    decimals: z.number().int().min(0).max(4).nullable().optional(),
    quickValues: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
    defaultValue: z.string().trim().max(80).nullable().optional(),
    loincCode: z.string().trim().regex(LOINC_CODE_PATTERN, 'Enter a LOINC code such as 2345-7').nullable().optional(),
    nameI18n: catalogueNameI18nSchema.optional(),
    sortOrder: z.number().int().min(0).max(9999).default(0),
    isActive: z.boolean().default(true),
});
export function referenceRangeProblems(ranges) {
    const problems = [];
    ranges.forEach((range, index) => {
        const at = `referenceRanges.${index}`;
        const min = range.minAgeDays ?? 0;
        const max = range.maxAgeDays ?? 365 * 200;
        if (min > max)
            problems.push({ path: `${at}.maxAgeDays`, message: 'The upper age must not be below the lower age' });
        const low = range.low ?? null;
        const high = range.high ?? null;
        if (low !== null && high !== null && low > high)
            problems.push({ path: `${at}.high`, message: 'The upper limit must not be below the lower limit' });
    });
    for (let a = 0; a < ranges.length; a += 1) {
        for (let b = a + 1; b < ranges.length; b += 1) {
            const first = ranges[a];
            const second = ranges[b];
            const sexesMeet = first.sex === 'any' || second.sex === 'any' || first.sex === second.sex;
            const agesMeet = (first.minAgeDays ?? 0) <= (second.maxAgeDays ?? 365 * 200) && (second.minAgeDays ?? 0) <= (first.maxAgeDays ?? 365 * 200);
            if (sexesMeet && agesMeet) {
                problems.push({ path: `referenceRanges.${b}`, message: `Range ${b + 1} overlaps range ${a + 1} for the same sex and age` });
            }
        }
    }
    return problems;
}
export function labTestProblems(input) {
    const problems = referenceRangeProblems(input.referenceRanges ?? []);
    const criticalLow = input.criticalLow ?? null;
    const criticalHigh = input.criticalHigh ?? null;
    if (criticalLow !== null && criticalHigh !== null && criticalLow >= criticalHigh) {
        problems.push({ path: 'criticalHigh', message: 'The critical high must be above the critical low' });
    }
    const reportableLow = input.reportableLow ?? null;
    const reportableHigh = input.reportableHigh ?? null;
    if (reportableLow !== null && reportableHigh !== null && reportableLow >= reportableHigh) {
        problems.push({ path: 'reportableHigh', message: 'The reportable range must run from a lower to a higher value' });
    }
    if (input.resultType === 'select') {
        const options = (input.selectOptions ?? []).map((option) => option.trim());
        if (options.length === 0 || options.some((option) => option === '')) {
            problems.push({ path: 'selectOptions', message: 'A select test needs at least one option, and none may be blank' });
        }
        if (new Set(options.map((option) => option.toLowerCase())).size !== options.length) {
            problems.push({ path: 'selectOptions', message: 'The select options must be different from each other' });
        }
    }
    return problems;
}
export const upsertLabPanelSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24).regex(CATALOGUE_CODE_PATTERN, CATALOGUE_CODE_MESSAGE),
    name: z.string().trim().min(2).max(200),
    testIds: z.array(z.string().uuid()).min(1).max(80),
    chargeItemId: z.string().uuid().nullable().optional(),
    basePriceMinor: moneyMinorSchema.min(0).default(0),
    isActive: z.boolean().default(true),
    nameI18n: catalogueNameI18nSchema.optional(),
});
export const createLabOrderSchema = z.object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    clinicalOrderId: z.string().uuid().nullable().optional(),
    erCaseId: z.string().uuid().nullable().optional(),
    collectAt: z.string().datetime().nullable().optional(),
    branchId: z.string().uuid().optional(),
    priority: z.enum(['routine', 'urgent', 'stat']).default('routine'),
    clinicalNotes: z.string().max(2000).optional(),
    referringDoctorName: z.string().trim().max(160).optional(),
    referringDoctorPhone: z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, 'Enter a valid phone number').optional(),
    payerId: z.string().uuid().nullable().optional(),
    diagnosisCode: z.string().trim().regex(ICD_CODE_PATTERN, 'Pick the diagnosis from the ICD search').nullable().optional(),
    diagnosisTitle: z.string().trim().min(2).max(300).nullable().optional(),
    referralNo: z.string().trim().min(1).max(60).nullable().optional(),
    referringLicense: z.string().trim().regex(/^[A-Za-z0-9./-]{2,40}$/, 'Enter the licence number without spaces').nullable().optional(),
    testIds: z.array(z.string().uuid()).max(100).default([]),
    panelIds: z.array(z.string().uuid()).max(50).default([]),
    /** Bill immediately at the lab reception (walk-in / direct patients). */
    createInvoice: z.boolean().default(true),
    billingExemption: z.enum(BILLING_EXEMPTION).optional(),
    exemptionReason: z.string().trim().min(3).max(300).optional(),
    homeCollection: z
        .object({
        address: z.string().trim().min(5).max(400),
        windowStart: z.string().datetime().optional(),
        windowEnd: z.string().datetime().optional(),
        notes: z.string().trim().max(400).optional(),
    })
        .nullable()
        .optional(),
}).refine((v) => v.testIds.length > 0 || v.panelIds.length > 0, {
    path: ['testIds'],
    message: 'Select at least one test or panel',
}).superRefine((v, ctx) => {
    if (v.diagnosisCode && !v.diagnosisTitle)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['diagnosisTitle'], message: 'Pick the diagnosis from the ICD search' });
    if (v.createInvoice)
        return;
    if (!v.billingExemption) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['billingExemption'], message: 'Say how an order without a bill is covered: credit, staff or free' });
    }
    if (!v.exemptionReason) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['exemptionReason'], message: 'Give the reason the order is not billed' });
    }
});
export const cancelLabOrderSchema = z.object({
    reason: z.string().trim().max(300).optional(),
});
export const cancelLabItemsSchema = z.object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(50),
    reason: z.string().trim().min(3).max(300),
});
export const addLabTestsSchema = z
    .object({
    testIds: z.array(z.string().uuid()).max(100).default([]),
    panelIds: z.array(z.string().uuid()).max(50).default([]),
})
    .refine((v) => v.testIds.length > 0 || v.panelIds.length > 0, {
    path: ['testIds'],
    message: 'Select at least one test or panel',
});
export const assignHomeCollectionSchema = z.object({
    phlebotomistId: z.string().uuid(),
});
export const failHomeCollectionSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
const queryFlag = z.preprocess((value) => value === true || value === 'true' || value === '1', z.boolean());
export const pageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: cursorText.optional(),
});
export const catalogueListQuerySchema = pageQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(200).default(100),
    q: z.string().trim().max(120).optional(),
    includeInactive: queryFlag.default(false),
});
export const homeCollectionQuerySchema = z.object({
    status: z.enum(HOME_COLLECTION_STATUS).optional(),
    mine: queryFlag.default(false),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: cursorText.optional(),
});
export const acknowledgeCriticalSchema = z.object({
    toldName: z.string().trim().min(2).max(120),
    readBackNote: z.string().trim().min(3).max(500),
});
export const criticalListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: cursorText.optional(),
});
export const linkChargesSchema = z.object({
    relinkInvoiceLines: z.boolean().default(false),
});
export const diagnosticsReportQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    groupBy: z.enum(['test', 'department']).default('test'),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(100000).default(0),
});
export const dateTimeRangeSchema = z
    .object({ from: z.string().datetime(), to: z.string().datetime() })
    .superRefine((value, ctx) => {
    const from = Date.parse(value.from);
    const to = Date.parse(value.to);
    if (to < from)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'The end must not be before the start' });
    else if ((to - from) / 86_400_000 > MAX_REPORT_RANGE_DAYS) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: `A report covers at most ${MAX_REPORT_RANGE_DAYS} days` });
    }
});
export const collectionIdentitySchema = z
    .object({
    patientId: z.string().uuid(),
    method: z.enum(COLLECTION_IDENTITY_METHODS),
    scanned: z.string().trim().max(80).optional(),
    value: z.string().trim().max(120).optional(),
    reason: z.string().trim().max(300).optional(),
})
    .superRefine((identity, ctx) => {
    if (identity.method === 'wristband_scan' && !identity.scanned) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scanned'], message: 'Scan the wristband' });
    }
    if (identity.method === 'two_identifiers' && !identity.value) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: 'Type the second identifier' });
    }
    if (identity.method === 'override' && (identity.reason ?? '').length < 5) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Say why the patient could not be identified' });
    }
});
export const collectSpecimenSchema = z.object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(MAX_BATCH_IDS),
    collectedAt: z.string().datetime().optional(),
    notes: z.string().max(600).optional(),
    identities: z.array(collectionIdentitySchema).max(50).optional(),
    paymentOverrideReason: z.string().trim().min(5).max(300).optional(),
});
export const collectHomeVisitSchema = z.object({
    identity: collectionIdentitySchema.optional(),
    paymentOverrideReason: z.string().trim().min(5).max(300).optional(),
});
export const receiveSpecimenSchema = z
    .object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(MAX_BATCH_IDS).optional(),
    accessionNo: z.string().trim().toUpperCase().min(3).max(60).optional(),
})
    .refine((value) => Boolean(value.orderItemIds?.length) !== Boolean(value.accessionNo), {
    message: 'Pick the specimens or scan one accession',
    path: ['accessionNo'],
});
export const rejectSpecimenSchema = z
    .object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(MAX_BATCH_IDS),
    reasonCode: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,40}$/).optional(),
    note: z.string().trim().max(300).optional(),
    reason: z.string().trim().min(1).max(300).optional(),
})
    .refine((value) => Boolean(value.reasonCode || value.reason), { path: ['reasonCode'], message: 'Pick why the specimen is rejected' });
export const verifyRadiologyReportSchema = z.object({
    release: z.boolean().default(true),
    overrideReason: z.string().trim().min(5).max(300).optional(),
});
export const labResultEntrySchema = z.object({
    orderItemId: z.string().uuid(),
    value: z.string().trim().max(2000),
    numericValue: z.number().nullable().optional(),
    unit: z.string().trim().max(24).optional(),
    flag: z.enum(LAB_RESULT_FLAG).optional(),
    comment: z.string().max(1000).optional(),
    overrideReason: z.string().trim().min(3).max(300).optional(),
    /** Microbiology only: organism plus antibiotic sensitivity grid. */
    culture: z
        .object({
        organism: z.string().trim().min(1, 'Name the organism, or write No growth').max(200),
        colonyCount: z.string().trim().max(80).optional(),
        sensitivities: z
            .array(z.object({
            antibiotic: z.string().trim().min(1).max(120),
            result: z.enum(['sensitive', 'intermediate', 'resistant']),
            mic: z.string().trim().max(40).optional(),
        }))
            .max(60)
            .default([]),
    })
        .nullable()
        .optional(),
});
export const enterLabResultSchema = z.object({
    results: z.array(labResultEntrySchema).min(1).max(200),
    qcOverrideReason: z.string().trim().min(5).max(300).optional(),
});
export const amendLabResultSchema = labResultEntrySchema.extend({
    reason: z.string().trim().min(5).max(500),
    qcOverrideReason: z.string().trim().min(5).max(300).optional(),
});
export const verifyLabResultSchema = z
    .object({
    orderItemIds: z.array(z.string().uuid()).min(1).max(MAX_BATCH_IDS),
    action: z.enum(['verify', 'reject', 'send_back', 'release', 'preliminary']).default('verify'),
    comment: z.string().max(1000).optional(),
    overrideReason: z.string().trim().min(5).max(300).optional(),
})
    .superRefine((value, ctx) => {
    if ((value.action === 'reject' || value.action === 'send_back') && (value.comment ?? '').trim().length < 5) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['comment'], message: 'Say why the result goes back to the bench (at least 5 characters)' });
    }
});
export const radiologyRetakeSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
export const secondOpinionRequestSchema = z.object({
    toUserId: z.string().uuid(),
    note: z.string().trim().max(500).optional(),
});
export const secondOpinionListQuerySchema = z.object({
    box: z.enum(['to', 'from']).default('to'),
    open: z.preprocess((value) => (value === undefined ? undefined : value === true || value === 'true' || value === '1'), z.boolean().optional()),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const secondOpinionAnswerSchema = z.object({
    answer: z.string().trim().min(3).max(2000),
});
export const radiologySettingsSchema = z.object({
    radiologySeparateVerifier: z.boolean().nullable(),
});
export const LABEL_REPRINT_REASONS = ['Printer jam', 'Damaged label', 'Extra tube'];
export const printLabelsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(60),
    copies: z.number().int().min(1).max(3).default(1),
    reason: z.string().trim().min(3).max(200).optional(),
});
export const reprintRequestSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(60),
    reason: z.string().trim().min(3).max(200),
});
export const reprintRequestListQuerySchema = z.object({
    status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const decideReprintRequestSchema = z.object({
    action: z.enum(['approve', 'reject']),
    note: z.string().trim().max(300).optional(),
});
export const LAB_EDIT_REQUEST_STATUS = ['pending', 'approved', 'rejected', 'cancelled'];
export const editRequestListQuerySchema = z.object({
    status: z.enum(LAB_EDIT_REQUEST_STATUS).default('pending'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().uuid().optional(),
});
export const decideEditRequestSchema = z
    .object({
    action: z.enum(['approve', 'reject']),
    note: z.string().trim().max(300).optional(),
})
    .refine((value) => value.action === 'approve' || (value.note ?? '').length >= 3, { path: ['note'], message: 'Say why the change is refused' });
export const rejectionReasonSchema = z.object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9_]{2,40}$/, 'Capital letters, digits and _'),
    label: z.string().trim().min(2).max(120),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const rematchUnmatchedSchema = z.object({
    limit: z.number().int().min(1).max(100).default(100),
    cursor: z.string().uuid().optional(),
});
// ── Analyser interfaces (SRS 8.1) ─────────────────────────────────────────────
export const upsertLabInstrumentSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24).regex(/^[A-Z0-9_-]+$/, 'Letters, digits, - and _'),
    name: z.string().trim().min(2).max(120),
    manufacturer: z.string().trim().max(120).optional(),
    protocol: z.enum(['hl7', 'astm', 'image-import']).default('hl7'),
    branchId: z.string().uuid().nullable().optional(),
    manualMode: z.boolean().default(false),
    isActive: z.boolean().default(true),
    notes: z.string().max(1000).optional(),
    connection: z.enum(['bridge', 'direct']).nullable().optional(),
    listenPort: z.number().int().min(41500).max(41599).nullable().optional(),
});
export const setInstrumentTestMapsSchema = z.object({
    maps: z
        .array(z.object({
        /** The code the analyser puts in OBX-3 / R-2, e.g. `HGB`. */
        analyserCode: z.string().trim().toUpperCase().min(1).max(40),
        testId: z.string().uuid(),
    }))
        .max(500),
});
export const resolveUnmatchedResultSchema = z
    .object({
    action: z.enum(['match', 'discard']),
    orderItemId: z.string().uuid().optional(),
    note: z.string().trim().max(500).optional(),
    rememberCode: z.boolean().default(false),
})
    .superRefine((value, ctx) => {
    if (value.action === 'match' && !value.orderItemId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['orderItemId'], message: 'Pick the specimen this result belongs to' });
    }
    if (value.action === 'discard' && !value.note?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['note'], message: 'Say why the result is being discarded' });
    }
});
export const labWorklistQuerySchema = z.object({
    status: z.enum(LAB_ORDER_ITEM_STATUS).optional(),
    /** Comma-separated statuses; a tab that spans two stages asks for both. */
    statuses: z.string().max(200).regex(/^[a-z_]+(,[a-z_]+)*$/, 'Comma-separated statuses').optional(),
    departmentGroup: z.string().max(40).optional(),
    patientId: z.string().uuid().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    q: z.string().trim().max(120).optional(),
    accessionNo: z.string().trim().max(60).optional(),
    referrer: z.string().trim().max(120).optional(),
    homeCollection: z.preprocess((value) => (value === undefined ? undefined : value === true || value === 'true' || value === '1'), z.boolean().optional()),
    onlyNormal: z.preprocess((value) => (value === undefined ? undefined : value === true || value === 'true' || value === '1'), z.boolean().optional()),
    dueNow: z.preprocess((value) => (value === undefined ? undefined : value === true || value === 'true' || value === '1'), z.boolean().optional()),
    source: z.enum(['opd', 'ipd', 'er', 'walkin', 'home']).optional(),
    wardId: z.string().uuid().optional(),
    branchId: z.string().uuid().optional(),
    sendOut: z.preprocess((value) => (value === undefined ? undefined : value === true || value === 'true' || value === '1'), z.boolean().optional()),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: cursorText.optional(),
});
export const collectionListQuerySchema = labWorklistQuerySchema.pick({ wardId: true, branchId: true, dueNow: true, source: true });
export const radiologyWorklistQuerySchema = z.object({
    status: z.string().max(30).optional(),
    modality: z.string().max(30).optional(),
    q: z.string().trim().max(120).optional(),
    referrer: z.string().trim().max(120).optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: cursorText.optional(),
});
export const upsertRadiologyStudyTypeSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24).regex(CATALOGUE_CODE_PATTERN, CATALOGUE_CODE_MESSAGE),
    name: z.string().trim().min(2).max(200),
    modality: z.enum(RADIOLOGY_MODALITY),
    bodyPart: z.string().trim().max(120).optional(),
    requiresContrast: z.boolean().default(false),
    requiresAppointment: z.boolean().default(false),
    durationMinutes: z.number().int().min(5).max(600).default(20),
    reportTemplate: z.string().max(20000).optional(),
    chargeItemId: z.string().uuid().nullable().optional(),
    basePriceMinor: moneyMinorSchema.min(0).default(0),
    isActive: z.boolean().default(true),
});
export const createRadiologyOrderSchema = z.object({
    patientId: z.string().uuid(),
    encounterId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    clinicalOrderId: z.string().uuid().nullable().optional(),
    erCaseId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().optional(),
    studyTypeIds: z.array(z.string().uuid()).min(1).max(20),
    priority: z.enum(['routine', 'urgent', 'stat']).default('routine'),
    clinicalNotes: z.string().max(2000).optional(),
    referringDoctorName: z.string().trim().max(160).optional(),
    referringDoctorPhone: z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, 'Enter a valid phone number').optional(),
    /** Mandatory prompt for females of childbearing age (SRS 8.2). */
    pregnancyChecked: z.boolean().default(false),
    payerId: z.string().uuid().nullable().optional(),
    createInvoice: z.boolean().default(true),
    singleInvoice: z.boolean().default(false),
    billingExemption: z.enum(BILLING_EXEMPTION).optional(),
    exemptionReason: z.string().trim().min(3).max(300).optional(),
}).superRefine((v, ctx) => {
    if (v.createInvoice)
        return;
    if (!v.billingExemption) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['billingExemption'], message: 'Say how an order without a bill is covered: credit, staff or free' });
    }
    if (!v.exemptionReason) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['exemptionReason'], message: 'Give the reason the order is not billed' });
    }
});
export const unmatchedListQuerySchema = pageQuerySchema.extend({
    status: z.enum(['pending', 'matched', 'discarded']).default('pending'),
});
export const modalityWorklistQuerySchema = z.object({
    modality: z.enum(RADIOLOGY_MODALITY).optional(),
    date: isoDateSchema.optional(),
});
export const criticalHistoryQuerySchema = pageQuerySchema.extend({
    from: isoDateSchema,
    to: isoDateSchema,
    kind: z.enum(['lab', 'radiology', 'all']).default('all'),
});
export const unbilledReportQuerySchema = pageQuerySchema.extend({
    from: isoDateSchema,
    to: isoDateSchema,
});
export const homeCollectionRequestSchema = z.object({
    address: z.string().trim().min(5).max(400),
    windowStart: z.string().datetime().optional(),
    windowEnd: z.string().datetime().optional(),
    notes: z.string().trim().max(400).optional(),
});
export const rescheduleHomeCollectionSchema = z.object({
    windowStart: z.string().datetime().optional(),
    windowEnd: z.string().datetime().optional(),
    address: z.string().trim().min(5).max(400).optional(),
    notes: z.string().trim().max(400).optional(),
});
export const labHomeFeeSchema = z.object({
    priceMinor: moneyMinorSchema.min(0),
});
export const labSettingsSchema = tenantSettingsSchema.pick({
    analyserResultsEditable: true,
    criticalResultEscalationPhone: true,
    separateVerifier: true,
    qcWindowHours: true,
    criticalEscalationMinutes: true,
    billOverlappingPanels: true,
    accessionPerOrder: true,
    releaseRequiresPayment: true,
    statCollectionMinutes: true,
    labelWidthMm: true,
    labelHeightMm: true,
    labelCommandLang: true,
    printBridgePort: true,
    reportReadySms: true,
    accessionMode: true,
    tubeSuffix: true,
    homeCollectionEnabled: true,
    collectionIdentityCheck: true,
    collectRequiresPayment: true,
    analyserOverride: true,
    labelReprintApproval: true,
    labelShowDob: true,
    labelShowNationalId: true,
    reportHeaderMode: true,
    preprintedTopMm: true,
    labReportLanguage: true,
    instrumentLogDays: true,
    publicPortalUrl: true,
});
export const qcLotListQuerySchema = catalogueListQuerySchema.omit({ q: true, includeInactive: true }).extend({
    testId: z.string().uuid().optional(),
});
export const qcChartQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(MAX_REPORT_RANGE_DAYS).default(30),
});
export const updateRadiologyStatusSchema = z.object({
    status: z.enum(RADIOLOGY_STATUS),
    scheduledAt: z.string().datetime().nullable().optional(),
    notes: z.string().max(600).optional(),
});
export const radiologyReportSchema = z.object({
    findings: z.string().trim().min(1).max(20000),
    impression: z.string().trim().max(8000).optional(),
    recommendation: z.string().max(4000).optional(),
    isAddendum: z.boolean().default(false),
    imageRefs: z.array(z.string().max(500)).max(50).default([]),
    criticalFinding: z.boolean().default(false),
    criticalFindingNote: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=diagnostics.js.map